import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass, Spinner, ArrowClockwise, Warning, FileText, PencilSimple, Plus, Trash, Receipt, EnvelopeSimple, SignIn, SignOut, User, Gear, ShieldCheck, Wrench, Archive, ShoppingCart } from "@phosphor-icons/react";
import { fetchLogbook, fetchLogbookSettings, updateLogbookSettings, fetchLogbookArchiveStatus, runLogbookArchive } from "@/lib/api";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";
import SortableTh, { nextSort, compareBy } from "@/components/SortableTh";
import { useT } from "@/lib/i18n";

const ACTION_ICON = {
  CREATE: { icon: Plus, color: "text-green-700 bg-green-50 border-green-200" },
  UPDATE: { icon: PencilSimple, color: "text-blue-700 bg-blue-50 border-blue-200" },
  DELETE: { icon: Trash, color: "text-red-700 bg-red-50 border-red-200" },
  EMAIL: { icon: EnvelopeSimple, color: "text-purple-700 bg-purple-50 border-purple-200" },
  EXPORT: { icon: FileText, color: "text-amber-700 bg-amber-50 border-amber-200" },
  LOGIN: { icon: SignIn, color: "text-slate-700 bg-slate-50 border-slate-200" },
  LOGOUT: { icon: SignOut, color: "text-slate-500 bg-slate-50 border-slate-200" },
  SYSTEM: { icon: Gear, color: "text-slate-500 bg-slate-100 border-slate-200" },
};

const ENTITY_ICON = {
  quote: Receipt, quote_line: Receipt,
  quote_return: ArrowClockwise, quote_return_line: ArrowClockwise,
  quote_warranty: ShieldCheck, quote_warranty_line: ShieldCheck,
  quote_service: Wrench, quote_service_line: Wrench,
  cart_checkout: ShoppingCart,
  part: FileText, customer: User,
  admin: User, auth: User, email: EnvelopeSimple, export: FileText,
};

// Convert UTC to EST (or EDT if observed) for display.
// US Eastern Time observes DST; use Intl to format correctly.
function formatEST(utcStr) {
  if (!utcStr) return "—";
  try {
    const d = new Date(utcStr.endsWith("Z") || utcStr.includes("+") ? utcStr : utcStr + "Z");
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false, timeZoneName: "short",
    }).format(d);
  } catch {
    return utcStr;
  }
}

const RESULT_BADGE = {
  success: { color: "text-green-700 bg-green-50 border-green-200", label: "DONE" },
  failed:  { color: "text-amber-700 bg-amber-50 border-amber-300", label: "FAILED" },
  error:   { color: "text-red-700 bg-red-50 border-red-300", label: "ERROR" },
  skipped: { color: "text-slate-500 bg-slate-50 border-slate-200", label: "SKIPPED" },
};

function parseDetails(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return { _text: String(raw) }; }
}

function DetailsPreview({ details }) {
  const d = parseDetails(details);
  if (!d) return <span className="text-slate-400">—</span>;
  if (d._text) return <span className="text-slate-600">{d._text}</span>;
  // Show key pairs compactly
  const pairs = [];
  if (d.changes) pairs.push(`changes: ${Object.entries(d.changes).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(", ")}`);
  if (d.new_total !== undefined) pairs.push(`new total: $${d.new_total}`);
  if (d.to) pairs.push(`to: ${d.to}`);
  if (d.subject) pairs.push(`"${d.subject}"`);
  if (d.status) pairs.push(`status: ${d.status}`);
  if (d.trigger) pairs.push(`trigger: ${d.trigger}`);
  if (d.items !== undefined) pairs.push(`${d.items} items`);
  if (d.product_code) pairs.push(`product: ${d.product_code}`);
  if (d.quantity !== undefined) pairs.push(`qty: ${d.quantity}`);
  if (d.price !== undefined) pairs.push(`price: $${d.price}`);
  if (d.removed) pairs.push(`removed: ${JSON.stringify(d.removed)}`);
  if (d.method) pairs.push(`method: ${d.method}`);
  if (d.message) pairs.push(d.message);
  const text = pairs.length ? pairs.join(" · ") : JSON.stringify(d);
  return <span className="text-slate-600 break-words">{text}</span>;
}

export default function LogbookPage({ user, onLogout }) {
  const tx = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actor, setActor] = useState("");
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [since, setSince] = useState("7");
  const [result, setResult] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loggingEnabled, setLoggingEnabled] = useState(true);
  const [togglingLog, setTogglingLog] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState(null);
  const [archiving, setArchiving] = useState(false);
  // Sort state — default: newest first (desc on timestamp)
  const [sortKey, setSortKey] = useState("ts_utc");
  const [sortDir, setSortDir] = useState("desc");
  const handleSort = (col) => {
    const { key, dir } = nextSort(sortKey, sortDir, col);
    setSortKey(key); setSortDir(dir);
  };

  useEffect(() => {
    fetchLogbookSettings().then(s => setLoggingEnabled(!!s.enabled)).catch(() => {});
    fetchLogbookArchiveStatus().then(s => setArchiveStatus(s.last)).catch(() => {});
  }, []);

  const handleArchiveNow = async () => {
    if (!user?.is_admin) return;
    if (!window.confirm("Archive the current active logbook? All entries will be copied to ActionLog_Archive and the active table will be truncated.")) return;
    setArchiving(true);
    try {
      const res = await runLogbookArchive(user.email);
      setArchiveStatus({ ok: true, rows: res.rows_archived, ran_at: res.ran_at, actor: user.email, trigger: "manual" });
      load();
    } catch (e) {
      toast.error(extractErr(e, "Archive failed").message);
    } finally {
      setArchiving(false);
    }
  };

  const toggleLogging = async () => {
    if (!user?.is_admin) return;
    const next = !loggingEnabled;
    if (!next && !window.confirm("Disable the logbook? All mutation events will stop being recorded until re-enabled.")) return;
    setTogglingLog(true);
    try {
      const res = await updateLogbookSettings(next, user.email);
      setLoggingEnabled(!!res.enabled);
      load();
    } catch (e) {
      toast.error(extractErr(e, "Failed to update logbook setting").message);
    } finally {
      setTogglingLog(false);
    }
  };

  // iter150AI.3 — Throttle + circuit-breaker for the /logbook endpoint.
  //
  // Why: filters live in controlled inputs (actor, entity, action, search,
  // result, since). Without protection, the user typing one character into
  // the search box fired one request per keystroke, and when the backend
  // 5xx-ed, the page kept hammering it through every filter change.
  //
  // What we do now:
  //   1. Debounce 400 ms — collapse rapid filter changes into one request.
  //   2. Track consecutive failures and back off (2 → 5 → 15 → 60 s ceiling)
  //      so a 5xx-ing backend gets exponentially less load.
  //   3. Surface a visible "Retrying in Xs…" banner so the user knows why
  //      the list looks stale instead of silently swallowing the error.
  //   4. Manual "Refresh" still bypasses the backoff (user-initiated).
  const [errorMsg, setErrorMsg] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0);  // seconds, for the banner
  // Refs that persist across renders and don't trigger re-renders.
  const failureCountRef = useRef(0);
  const nextAllowedAtRef = useRef(0);  // ms epoch — earliest time we may fetch again
  const debounceTimerRef = useRef(null);
  const reqIdRef = useRef(0);  // monotonic id — drop stale responses
  const retryTimerRef = useRef(null);

  const BACKOFF_LADDER_MS = [2_000, 5_000, 15_000, 30_000, 60_000];
  function _backoffMs(failures) {
    const idx = Math.min(failures - 1, BACKOFF_LADDER_MS.length - 1);
    return BACKOFF_LADDER_MS[Math.max(idx, 0)];
  }

  const fetchNow = useCallback(async (opts = {}) => {
    const force = !!opts.force;
    const now = Date.now();
    if (!force && now < nextAllowedAtRef.current) {
      // Don't fire — circuit breaker open.
      const wait = Math.ceil((nextAllowedAtRef.current - now) / 1000);
      setRetryAfter(wait);
      return;
    }
    const myId = ++reqIdRef.current;
    setLoading(true);
    setRetryAfter(0);
    const params = {};
    if (actor) params.actor = actor;
    if (entity) params.entity = entity;
    if (action) params.action = action;
    if (search) params.search = search;
    if (result) params.result = result;
    if (since && since !== "all") params.since_days = since;
    try {
      const d = await fetchLogbook(params);
      if (myId !== reqIdRef.current) return;  // stale response, ignore
      setData(d);
      setErrorMsg(null);
      failureCountRef.current = 0;
      nextAllowedAtRef.current = 0;
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      failureCountRef.current += 1;
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e?.message || "Logbook request failed";
      // iter150AI.3 — honour Retry-After on 429 (backend rate-limiter).
      // Otherwise fall back to our exponential ladder.
      const retryHeader = parseInt(e?.response?.headers?.["retry-after"] || "0", 10);
      let wait;
      if (status === 429 && retryHeader > 0) {
        wait = retryHeader * 1000;
      } else {
        wait = _backoffMs(failureCountRef.current);
      }
      nextAllowedAtRef.current = Date.now() + wait;
      setErrorMsg({ status, msg, failures: failureCountRef.current });
      setData({ items: [], total: 0, table_exists: true });
      setRetryAfter(Math.ceil(wait / 1000));
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [actor, entity, action, search, since, result]);

  // Public "load" — kept stable for callers (toggleLogging, archive button,
  // refresh button). Uses the debouncer when called from filter effects.
  const load = useCallback(() => { fetchNow({ force: true }); }, [fetchNow]);

  // Debounced auto-fetch when any filter changes.
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => { fetchNow(); }, 400);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [fetchNow]);

  // Live countdown for the retry-after banner.
  useEffect(() => {
    if (retryAfter <= 0) return;
    if (retryTimerRef.current) clearInterval(retryTimerRef.current);
    retryTimerRef.current = setInterval(() => {
      setRetryAfter((r) => {
        if (r <= 1) {
          clearInterval(retryTimerRef.current);
          // Cooldown expired — try once more automatically.
          fetchNow();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(retryTimerRef.current);
  }, [retryAfter, fetchNow]);

  const items = data?.items || [];
  const tableExists = data?.table_exists !== false;

  // Client-side sort: key → accessor
  const ACCESSORS = {
    ts_utc: (r) => r.ts_utc,
    actor: (r) => (r.actor_name || r.actor_email || ""),
    action_type: (r) => r.action_type,
    entity: (r) => r.entity,
    target: (r) => (r.entity_label || r.entity_id || ""),
    result: (r) => r.result || "success",
  };
  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const acc = ACCESSORS[sortKey];
    if (!acc) return items;
    const mult = sortDir === "desc" ? -1 : 1;
    return [...items].sort((a, b) => mult * compareBy(a, b, acc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortKey, sortDir]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white" data-testid="logbook-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="text-slate-500 hover:text-blue-700 flex items-center gap-1 text-sm font-medium" data-testid="logbook-back">
          <ArrowLeft size={16} /> Home
        </Link>
        <h1 className="text-lg md:text-xl font-bold font-['Chivo'] text-slate-900 flex-1">Logbook</h1>
        <button onClick={load} data-testid="logbook-refresh"
          className="w-9 h-9 flex items-center justify-center rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-50">
          <ArrowClockwise size={16} className={loading ? "animate-spin" : ""} />
        </button>
        {user?.is_admin && (
          <button onClick={toggleLogging} disabled={togglingLog} data-testid="logbook-toggle"
            className={`h-9 px-3 flex items-center gap-1.5 rounded-sm border text-xs font-semibold transition-colors ${
              loggingEnabled
                ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            } ${togglingLog ? "opacity-60" : ""}`}
            title={loggingEnabled ? "Logging is ON — click to disable" : "Logging is OFF — click to enable"}>
            <span className={`inline-block w-2 h-2 rounded-full ${loggingEnabled ? "bg-green-500 animate-pulse" : "bg-amber-500"}`} />
            {togglingLog ? "…" : (loggingEnabled ? "Logging ON" : "Logging OFF")}
          </button>
        )}
        {user?.is_admin && (
          <button onClick={handleArchiveNow} disabled={archiving} data-testid="logbook-archive-now"
            className={`h-9 px-3 flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700 ${archiving ? "opacity-60 cursor-wait" : ""}`}
            title={archiveStatus?.ran_at ? `Last archived: ${new Date(archiveStatus.ran_at).toLocaleString()} (${archiveStatus.rows} rows by ${archiveStatus.actor})` : "Never archived yet"}>
            <Archive size={13} weight="bold" />
            {archiving ? "Archiving…" : "Archive now"}
          </button>
        )}
        <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
          <User size={14} /> {user?.email}
        </div>
      </header>

      {!loggingEnabled && (
        <div className="mx-4 md:mx-6 mt-4 bg-amber-50 border-2 border-amber-300 rounded-sm p-3 flex items-start gap-3" data-testid="logbook-disabled-banner">
          <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="font-bold text-amber-900">{tx("logbook.logging_disabled", "Logging is currently disabled")}</p>
            <p className="text-amber-800 mt-0.5">{tx("logbook.logging_disabled_hint", "New events are not being recorded. Existing entries are preserved.")}</p>
          </div>
          {user?.is_admin && (
            <button onClick={toggleLogging} disabled={togglingLog}
              data-testid="logbook-enable-inline-button"
              className={`shrink-0 h-9 px-4 rounded-sm bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase tracking-wider transition-colors ${togglingLog ? "opacity-60" : ""}`}>
              {togglingLog ? "Enabling…" : "Enable Logging"}
            </button>
          )}
        </div>
      )}

      {!tableExists && loggingEnabled && (
        <div className="mx-4 md:mx-6 mt-4 bg-amber-50 border-2 border-amber-300 rounded-sm p-4" data-testid="logbook-setup-banner">
          <div className="flex items-start gap-3">
            <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-amber-900">{tx("logbook.table_not_created", "Logbook table not yet created")}</p>
              <p className="text-amber-800 mt-1">
                The <code className="px-1 bg-amber-100 rounded">ActionLog</code> table must be created in your Bluehost MySQL database. All mutation events are being buffered and will start flowing once the table exists.
              </p>
              <p className="text-amber-800 mt-2 font-semibold">Steps:</p>
              <ol className="list-decimal list-inside text-amber-800 mt-1 space-y-0.5">
                <li>Open Bluehost cPanel → phpMyAdmin → <code className="px-1 bg-amber-100 rounded">ipqhznmy_UsAir</code></li>
                <li>{tx("logbook.step_click_sql", "Click the \"SQL\" tab")}</li>
                <li>Paste &amp; run the script from <code className="px-1 bg-amber-100 rounded">/app/backend/sql/create_action_log.sql</code></li>
                <li>{tx("logbook.step_refresh", "Refresh this page — logs will start appearing")}</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-4 md:px-6 py-4 grid grid-cols-2 md:grid-cols-7 gap-2">
        <div className="col-span-2 md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="logbook-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={tx("logbook.search_placeholder", "Search details...")}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <input data-testid="logbook-actor" value={actor} onChange={(e) => setActor(e.target.value)}
          placeholder={tx("logbook.actor_placeholder", "Actor email...")}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <select data-testid="logbook-entity" value={entity} onChange={(e) => setEntity(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="">{tx("logbook.all_entities", "All entities")}</option>
          <option value="quote">Quote</option>
          <option value="quote_line">{tx("logbook.quote_line", "Quote line")}</option>
          <option value="quote_return">Return</option>
          <option value="quote_return_line">{tx("logbook.return_line", "Return line")}</option>
          <option value="quote_warranty">Warranty</option>
          <option value="quote_warranty_line">{tx("logbook.warranty_line", "Warranty line")}</option>
          <option value="quote_service">Service</option>
          <option value="quote_service_line">{tx("logbook.service_line", "Service line")}</option>
          <option value="customer">Customer</option>
          <option value="part">Part</option>
          <option value="admin">Admin</option>
          <option value="auth">Auth</option>
          <option value="email">Email</option>
          <option value="export">Export</option>
        </select>
        <select data-testid="logbook-action" value={action} onChange={(e) => setAction(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="">{tx("logbook.all_actions", "All actions")}</option>
          <option>CREATE</option>
          <option>UPDATE</option>
          <option>DELETE</option>
          <option>EMAIL</option>
          <option>EXPORT</option>
          <option>LOGIN</option>
          <option>LOGOUT</option>
        </select>
        <select data-testid="logbook-since" value={since} onChange={(e) => setSince(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="1">Last 24h</option>
          <option value="7">{tx("hot_leads.last_7_days", "Last 7 days")}</option>
          <option value="30">{tx("hot_leads.last_30_days", "Last 30 days")}</option>
          <option value="90">{tx("logbook.last_90_days", "Last 90 days")}</option>
          <option value="all">{tx("logbook.all_time", "All time")}</option>
        </select>
        <select data-testid="logbook-result" value={result} onChange={(e) => setResult(e.target.value)}
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500">
          <option value="">{tx("logbook.all_results", "All results")}</option>
          <option value="success">{tx("logbook.result_done", "Done")}</option>
          <option value="failed">Failed</option>
          <option value="error">Error</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>

      {/* iter150AI.3 — Error banner with circuit-breaker countdown. */}
      {errorMsg && (
        <div className="px-4 md:px-6 pt-3"
             data-testid="logbook-error-banner">
          <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-800 text-xs font-semibold px-3 py-2 rounded-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <Warning size={14} weight="bold" />
              <span>
                Logbook fetch failed
                {errorMsg.status ? ` (HTTP ${errorMsg.status})` : ""}
                {errorMsg.failures > 1 ? ` — ${errorMsg.failures} consecutive failures` : ""}
                . {errorMsg.msg}
              </span>
              {retryAfter > 0 && (
                <span className="font-mono text-[11px] bg-red-100 px-1.5 py-0.5 rounded-sm"
                      data-testid="logbook-retry-after">
                  retrying in {retryAfter}s
                </span>
              )}
            </div>
            <button onClick={load}
                    data-testid="logbook-retry-now"
                    className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm border border-red-300 hover:bg-red-100">
              Retry now
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="px-4 md:px-6 pb-8">
        <p className="text-xs text-slate-500 mb-2">
          {loading ? "Loading..." : `${data?.total ?? 0} entries · showing ${items.length}`}
        </p>
        <div className="border border-slate-200 rounded-sm bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-[52px] z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_0_rgb(226_232_240)]">
              <tr className="text-left">
                <SortableTh column="ts_utc" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap bg-slate-50">{tx("logbook.col_timestamp", "Timestamp (EST)")}</SortableTh>
                <SortableTh column="actor" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{tx("logbook.col_actor", "Actor")}</SortableTh>
                <SortableTh column="action_type" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{tx("logbook.col_action", "Action")}</SortableTh>
                <SortableTh column="entity" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{tx("logbook.col_entity", "Entity")}</SortableTh>
                <SortableTh column="target" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{tx("logbook.col_target", "Target")}</SortableTh>
                <SortableTh column="result" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{tx("logbook.col_result", "Result")}</SortableTh>
                <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">{tx("table.details", "Details")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400"><Spinner size={18} className="animate-spin inline-block mr-2" />Loading...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400">{tx("logbook.no_entries", "No entries match these filters")}</td></tr>
              )}
              {sortedItems.map((row) => {
                const meta = ACTION_ICON[row.action_type] || ACTION_ICON.SYSTEM;
                const Icon = meta.icon;
                const EIcon = ENTITY_ICON[row.entity] || FileText;
                const resultKey = (row.result || "success").toLowerCase();
                const resMeta = RESULT_BADGE[resultKey] || RESULT_BADGE.success;
                const isOpen = expanded === row.id;
                return (
                  <tr key={row.id} data-testid={`logbook-row-${row.id}`}
                    onClick={() => setExpanded(isOpen ? null : row.id)}
                    className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50/70 ${isOpen ? "bg-blue-50/40" : ""} ${resultKey !== "success" ? "bg-amber-50/30" : ""}`}>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-600 whitespace-nowrap">{formatEST(row.ts_utc)}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-800 font-medium truncate max-w-[180px]" title={row.actor_email}>
                        {row.actor_name || row.actor_email}
                      </div>
                      {row.actor_name && (
                        <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{row.actor_email}</div>
                      )}
                      {row.actor_role && <div className="text-[10px] text-slate-400 uppercase tracking-wider">{row.actor_role}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${meta.color}`}>
                        <Icon size={10} weight="bold" />{row.action_type}
                      </span>
                      {row.trigger_type === "automated" && (
                        <span className="ml-1 text-[9px] text-slate-400 italic">auto</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <span className="inline-flex items-center gap-1"><EIcon size={12} className="text-slate-400" />{row.entity}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[220px] truncate">
                      {(row.entity === "cart_checkout" || row.entity?.startsWith("quote")) && row.entity_id ? (
                        <Link
                          data-testid={`logbook-view-quote-${row.entity_id}`}
                          to={`/quote/${encodeURIComponent(row.entity_id)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-700 hover:underline font-medium truncate inline-block max-w-full"
                          title={`Open quote ${row.entity_id}`}
                        >
                          {row.entity_label || row.entity_id}
                        </Link>
                      ) : (
                        row.entity_label || row.entity_id || "—"
                      )}
                    </td>
                    <td className="px-3 py-2" data-testid={`logbook-result-${row.id}`}>
                      <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${resMeta.color}`}>
                        {resMeta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs max-w-[400px]">
                      {row.error_message && (
                        <div className="text-[11px] text-red-700 font-medium mb-1 break-words">{row.error_message}</div>
                      )}
                      {isOpen ? (
                        <pre className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap break-all max-h-56 overflow-y-auto">
                          {JSON.stringify(parseDetails(row.details) || {}, null, 2)}
                        </pre>
                      ) : (
                        <DetailsPreview details={row.details} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
